// central.eu — shared design tokens, expressed in TypeScript.
// Theme-varying colors go through cssManager.bdTheme(bright, dark) so components
// restyle automatically when domtools.themeManager toggles bright/dark.

import { cssManager, css, unsafeCSS, type CSSResult } from '@design.estate/dees-element';

/** Theme-aware color tokens (bright value, dark value). */
export const t = {
  canvas: cssManager.bdTheme('#f5f5f7', '#000000'),
  card: cssManager.bdTheme('#ffffff', '#1c1c1e'),
  card2: cssManager.bdTheme('#fbfbfd', '#2c2c2e'),
  ink: cssManager.bdTheme('#1d1d1f', '#f5f5f7'),
  ink2: cssManager.bdTheme('#4a4a4f', '#c7c7cc'),
  muted: cssManager.bdTheme('#86868b', '#8e8e93'),
  hairline: cssManager.bdTheme('rgba(0,0,0,.09)', 'rgba(255,255,255,.13)'),
  hairline2: cssManager.bdTheme('rgba(0,0,0,.055)', 'rgba(255,255,255,.08)'),
  panel2: cssManager.bdTheme('#f0f0f3', '#2c2c2e'),
  eu: cssManager.bdTheme('#0033A0', '#6a8cff'),
  euBrand: cssManager.bdTheme('#0033A0', '#3a63d8'),
  amber: cssManager.bdTheme('#c47b00', '#e0a040'),
  green: cssManager.bdTheme('#1a8a4b', '#5bb36b'),
  euFill: cssManager.bdTheme('#e8eaf2', '#1a1e2e'),
  shadowSm: cssManager.bdTheme(
    '0 1px 2px rgba(0,0,0,.04), 0 6px 16px rgba(0,0,0,.05)',
    '0 1px 2px rgba(0,0,0,.5)',
  ),
  shadow: cssManager.bdTheme(
    '0 2px 8px rgba(0,0,0,.05), 0 18px 50px rgba(0,0,0,.09)',
    '0 10px 50px rgba(0,0,0,.6)',
  ),
};

/** Non-theme design constants. */
export const ffDisplay = unsafeCSS(
  `-apple-system, BlinkMacSystemFont, "SF Pro Display", "Inter Tight", system-ui, sans-serif`,
);
export const ffText = unsafeCSS(
  `-apple-system, BlinkMacSystemFont, "SF Pro Text", "Inter Tight", system-ui, sans-serif`,
);
export const ffMono = unsafeCSS(`"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace`);

export const radius = unsafeCSS('20px');
export const radiusSm = unsafeCSS('13px');
export const radiusXs = unsafeCSS('9px');
export const pill = unsafeCSS('980px');
export const maxw = unsafeCSS('1320px');
export const padX = unsafeCSS('clamp(20px, 4vw, 56px)');

/**
 * Maps the design's token names to CSS custom properties on :host, fed by the
 * bright/dark bdTheme values. Because custom properties inherit into the shadow
 * tree, view CSS and inline SVGs can use var(--eu) etc. verbatim and stay
 * theme-reactive — all defined here in TypeScript, no stylesheet file.
 */
export const tokenHostVars: CSSResult = css`
  :host {
    --canvas: ${t.canvas};
    --card: ${t.card};
    --card-2: ${t.card2};
    --ink: ${t.ink};
    --ink-2: ${t.ink2};
    --muted: ${t.muted};
    --hairline: ${t.hairline};
    --hairline-2: ${t.hairline2};
    --eu: ${t.eu};
    --eu-brand: ${t.euBrand};
    --eu-dim: ${cssManager.bdTheme('#00246f', '#9fb4ff')};
    --amber: ${t.amber};
    --green: ${t.green};
    --paper: var(--canvas);
    --paper-2: ${cssManager.bdTheme('#ececef', '#1c1c1e')};
    --panel: var(--card);
    --panel-2: ${t.panel2};
    --rule: var(--hairline);
    --rule-2: var(--hairline-2);
    --radius: 20px;
    --radius-sm: 13px;
    --radius-xs: 9px;
    --pill: 980px;
    --shadow-sm: ${t.shadowSm};
    --shadow: ${t.shadow};
    --ff-display: ${ffDisplay};
    --ff-text: ${ffText};
    --ff-mono: ${ffMono};
    --unit: 8px;
    --pad-x: clamp(20px, 4vw, 56px);
    --maxw: 1320px;
  }
`;

/** Reset + base typography shared by all view components. */
export const baseStyles: CSSResult = css`
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
  a {
    color: inherit;
    text-decoration: none;
  }
  a.link {
    border-bottom: 1px solid ${t.hairline};
    padding-bottom: 1px;
  }
  a.link:hover {
    border-bottom-color: ${t.eu};
    color: ${t.eu};
  }
  button {
    font: inherit;
    color: inherit;
    background: none;
    border: none;
    cursor: pointer;
  }
  .mono {
    font-family: ${ffMono};
  }
  .lucide {
    width: 1em;
    height: 1em;
    stroke-width: 2;
    display: inline-block;
    vertical-align: middle;
  }
`;
